import React, { useMemo, useState } from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Tab,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { DayCalendarSkeleton, PickersDay } from "@mui/x-date-pickers";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Typography from "@mui/material/Typography";
import './meals.css';

const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function MealDialogEditDates({ isOpen, onClose, eventMeal, setEventMeal }) {
    const { PA_GlobalObject } = usePA_GlobalStore();

    const [eventHasDates] = useState(() => PA_GlobalObject?.Planning?.time_table?.dates?.length > 0);
    const [eventHasDays] = useState(() => PA_GlobalObject?.Planning?.time_table?.days?.length > 0);

    const [mealTimeTable, setMealTimeTable] = useState(eventMeal.time_table || { dates: [], days: [] });
    const [mealName, setMealName] = useState(eventMeal.name);
    const [tabValue, setTabValue] = useState(eventHasDates || !eventHasDays ? "date" : "day");

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const [calendarMealDate, setCalendarMealDate] = useState(() =>
        mealTimeTable?.dates?.length > 0 ? dayjs(mealTimeTable.dates[0].date, "YYYY-MM-DD") : null
    );

    const [selectedMealDay, setSelectedMealDay] = useState(() => {
        const daysArray = mealTimeTable?.days;
        if (Array.isArray(daysArray) && daysArray.length > 0) {
            const dayObj = daysArray[0];
            const key = Object.keys(dayObj).find(k => k.startsWith("day "));
            return key ? { [key]: dayObj[key] } : {};
        }
        return {};
    });

    const rawBeginTime = mealTimeTable?.dates?.[0]?.begin_time || mealTimeTable?.days?.[0]?.begin_time || null;
    const rawEndTime = mealTimeTable?.dates?.[0]?.end_time || mealTimeTable?.days?.[0]?.end_time || null;

    const [selectedMealBeginTime, setSelectedMealBeginTime] = useState(() => rawBeginTime ? dayjs(rawBeginTime, 'h:mm A') : null);
    const [selectedMealEndTime, setSelectedMealEndTime] = useState(() => rawEndTime ? dayjs(rawEndTime, 'h:mm A') : null);

    function ServerDay(props) {
        const { day, outsideCurrentMonth, planningData, ...other } = props;
        const isSelected = dayjs(day).isSame(calendarMealDate, 'day');
        const eventDates = planningData?.time_table?.dates?.map(d => dayjs(d.date, "YYYY-MM-DD")) || [];
        const isEventDate = eventDates.some(eventDay => eventDay.isSame(day, "day"));

        return (
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                onClick={() => handleDateClick(day)}
                sx={{
                    transition: 'all 0.2s ease-in-out',
                    ...(isSelected
                        ? {
                            backgroundColor: "#1976d2 !important",
                            color: "white !important",
                            borderRadius: "50%",
                            boxShadow: '0 4px 10px rgba(25, 118, 210, 0.4)',
                        }
                        : {
                            backgroundColor: "transparent",
                            color: outsideCurrentMonth ? "#ccc" : "text.primary",
                            border: dayjs().isSame(dayjs(day), 'day') ? '1px solid #1976d2' : 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                        }),
                    position: "relative",
                    borderBottom: isEventDate && !isSelected ? "3px solid #1976d2" : "none",
                }}
            >
                <span>{day.format("D")}</span>
            </PickersDay>
        );
    }

    const handleDateClick = (_date) => {
        const formattedDate = _date.format('YYYY-MM-DD');
        setCalendarMealDate(_date);

        const planningDates = PA_GlobalObject?.Planning?.time_table?.dates || [];
        const index = planningDates.findIndex(d => d.date === formattedDate);
        const dayKey = `day ${index !== -1 ? index + 1 : 1}`;
        const dayName = planningDates[index]?.dayOfWeek ?? _date.format('dddd');

        setMealTimeTable(prev => {
            const prevDateEntry = prev?.dates?.[0] || {};
            const prevDayEntry = prev?.days?.[0] || {};
            return {
                ...prev,
                dates: [{ ...prevDateEntry, date: formattedDate }],
                days: [{ ...prevDayEntry, [dayKey]: dayName }]
            };
        });

        setSelectedMealDay({ [dayKey]: dayName });
    };

    const handleMealDayChange = (dayKey, dayName) => {
        setSelectedMealDay({ [dayKey]: dayName });
        const matchingDate = PA_GlobalObject?.Planning?.time_table?.dates?.find(d => d.dayOfWeek === dayName)?.date;

        if (matchingDate) {
            setCalendarMealDate(dayjs(matchingDate));
        }

        setMealTimeTable(prev => {
            const prevDateEntry = prev?.dates?.[0] || {};
            const prevDayEntry = prev?.days?.[0] || {};
            return {
                ...prev,
                ...(matchingDate && { dates: [{ ...prevDateEntry, date: matchingDate }] }),
                days: [{ ...prevDayEntry, [dayKey]: dayName }]
            };
        });
    };

    const handleBeginTimeChange = (newTime) => {
        if (!newTime) return;
        const formatted = newTime.format('h:mm A');
        setMealTimeTable(prev => {
            const prevDateEntry = prev?.dates?.[0] || {};
            const prevDayEntry = prev?.days?.[0] || {};
            return {
                ...prev,
                dates: [{ ...prevDateEntry, begin_time: formatted }],
                days: [{ ...prevDayEntry, begin_time: formatted }]
            };
        });
    };

    const handleEndTimeChange = (newTime) => {
        if (!newTime) return;
        const formatted = newTime.format('h:mm A');
        setMealTimeTable(prev => {
            const prevDateEntry = prev?.dates?.[0] || {};
            const prevDayEntry = prev?.days?.[0] || {};
            return {
                ...prev,
                dates: [{ ...prevDateEntry, end_time: formatted }],
                days: [{ ...prevDayEntry, end_time: formatted }]
            };
        });
    };

    const renderMealDaysLayoutForBegin = () => {
        const planningDays = PA_GlobalObject?.Planning?.time_table?.days || [];

        if (planningDays.length === 0) {
            return (
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: '6px', pb: 2 }}>
                    {daysOfWeek.map((day, index) => (
                        <Box key={index} sx={{ flex: 1, aspectRatio: '1 / 1', maxWidth: 'calc((100% - 6 * 6px) / 7)' }}>
                            <Chip
                                label={<Typography variant="body2">{day.slice(0, 2)}</Typography>}
                                sx={{
                                    width: '100%', height: '100%',
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.16)' }
                                }}
                                clickable
                                onClick={() => handleMealDayChange(`day ${index + 1}`, day)}
                            />
                        </Box>
                    ))}
                </Box>
            );
        }

        const calendarDays = planningDays.map((dayObj, index) => {
            const dayKey = Object.keys(dayObj).find(k => k.startsWith('day '));
            const dayName = dayObj[dayKey];
            return { key: dayKey, name: dayName, index: index + 1 };
        });

        const weeks = [];
        let currentWeek = Array(7).fill(null);

        calendarDays.forEach((dayObj) => {
            const dayOfWeekIndex = daysOfWeek.indexOf(dayObj.name);
            if (dayOfWeekIndex !== -1) {
                currentWeek[dayOfWeekIndex] = dayObj;
                if (dayOfWeekIndex === 6) {
                    weeks.push(currentWeek);
                    currentWeek = Array(7).fill(null);
                }
            }
        });
        if (currentWeek.some(day => day !== null)) weeks.push(currentWeek);

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pb: 2 }}>
                {weeks.map((week, weekIndex) => (
                    <Box key={weekIndex} sx={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                        {week.map((day, dayIndex) => {
                            const isSelected = day && Object.keys(selectedMealDay).find(k => k.startsWith('day ')) === day.key;
                            return (
                                <Box key={dayIndex} sx={{ flex: '1 1 calc(100% / 7)', maxWidth: '45px', aspectRatio: '1', display: 'flex', justifyContent: 'center' }}>
                                    {day ? (
                                        <Chip
                                            label={<Typography variant="body2" sx={{ fontWeight: isSelected ? '700' : '500' }}>{day.name.slice(0, 2)}</Typography>}
                                            sx={{
                                                width: '100%', height: '100%',
                                                backgroundColor: isSelected ? '#1976d2 !important' : 'rgba(25, 118, 210, 0.08) !important',
                                                color: isSelected ? '#fff !important' : 'text.primary',
                                                borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                boxShadow: isSelected ? '0 4px 8px rgba(25, 118, 210, 0.3)' : 'none',
                                                transition: 'all 0.2s',
                                                '&:hover': { backgroundColor: isSelected ? '#1565c0 !important' : 'rgba(25, 118, 210, 0.16) !important' }
                                            }}
                                            clickable
                                            onClick={() => handleMealDayChange(day.key, day.name)}
                                        />
                                    ) : (
                                        <Box sx={{ width: '100%', height: '100%', border: '1px dashed #e0e0e0', borderRadius: '12px' }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        );
    };

    const initialDate = useMemo(() => {
        const timeTableDate = eventMeal?.time_table?.dates?.[0]?.date;
        if (timeTableDate && dayjs(timeTableDate).isValid()) return dayjs(timeTableDate, "YYYY-MM-DD");
        const planningDate = PA_GlobalObject?.Planning?.time_table?.dates?.[0]?.date;
        if (planningDate && dayjs(planningDate).isValid()) return dayjs(planningDate, "YYYY-MM-DD");
        return dayjs();
    }, [eventMeal, PA_GlobalObject]);

    const saveEventMealSettings = () => {

        const updatedMeal = {
            ...eventMeal,
            name: mealName,
            time_table: mealTimeTable
        };

        console.log("the updated meal time_table : ", updatedMeal);

        setEventMeal(updatedMeal);
        onClose();
    };

    const mealCategories = [
        { id: 'breakfast', title: 'Breakfast', icon: 'icon-fried-egg' },
        { id: 'brunch', title: 'Brunch', icon: 'icon-toast' },
        { id: 'lunch', title: 'Lunch', icon: 'icon-burger' },
        { id: 'snack', title: 'Snack', icon: 'icon-apple' },
        { id: 'dinner', title: 'Dinner', icon: 'icon-platter' },
        { id: 'dessert', title: 'Dessert', icon: 'icon-ice-cream' },
    ];

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 360,
                    maxWidth: 460,
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
                },
            }}
        >
            <DialogTitle sx={{ color: '#151269', textAlign: 'center', backgroundColor: '#f0f7ff', fontWeight: '600', p: 2 }}>
                Meal Scheduler
            </DialogTitle>
            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: '#151269' }}>
                <i className="icon-cross" style={{ fontSize: '16px' }} />
            </IconButton>

            <DialogContent sx={{ p: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
                <TabContext value={tabValue}>
                    <TabList sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }} centered variant="fullWidth" onChange={handleTabChange}>
                        <Tab label="Specific Date" value="date" sx={{ fontWeight: '600' }} />
                        <Tab label="Day of Event" value="day" sx={{ fontWeight: '600' }} />
                    </TabList>

                    <TabPanel value="date" sx={{ p: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <DateCalendar
                                sx={{ width: '100%', height: 'auto' }}
                                value={calendarMealDate}
                                onChange={(newValue) => setCalendarMealDate(newValue)}
                                defaultValue={initialDate}
                                referenceDate={initialDate}
                                renderLoading={() => <DayCalendarSkeleton />}
                                showDaysOutsideCurrentMonth
                                slots={{ day: ServerDay }}
                                slotProps={{
                                    day: { planningData: PA_GlobalObject?.Planning },
                                }}
                            />
                        </Box>
                    </TabPanel>

                    <TabPanel value="day" sx={{ p: 1 }}>
                        {eventHasDays ? renderMealDaysLayoutForBegin() : <Typography variant="body2" color="text.secondary" align="center">No dynamic days found in current roadmap.</Typography>}
                    </TabPanel>
                </TabContext>

                <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
                    <FormControl fullWidth>
                        <TimePicker
                            label="Begin Time"
                            value={selectedMealBeginTime}
                            onChange={(newTime) => {
                                setSelectedMealBeginTime(newTime);
                                handleBeginTimeChange(newTime);
                            }}
                            ampmInClock
                            minutesStep={5}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TimePicker
                            label="End Time"
                            value={selectedMealEndTime}
                            onChange={(newTime) => {
                                setSelectedMealEndTime(newTime);
                                handleEndTimeChange(newTime);
                            }}
                            ampmInClock
                            minutesStep={5}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </FormControl>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: '600', color: '#151269', mb: 1 }}>Select Meal Category</Typography>
                <Box sx={{ display: 'flex', gap: '10px', overflowX: 'auto', pb: 1, pt: 0.5, '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e0e0e0', borderRadius: '4px' } }}>
                    {mealCategories.map((meal) => {
                        const isSelected = mealName?.toLowerCase() === meal.id;
                        return (
                            <IconButton
                                key={meal.id}
                                sx={{
                                    display: 'flex', flexDirection: 'column', gap: '4px', p: '10px',
                                    minWidth: '72px', borderRadius: '12px',
                                    boxShadow: isSelected ? '0 6px 14px rgba(240, 150, 55, 0.25)' : '0 2px 6px rgba(0,0,0,0.05)',
                                    backgroundColor: isSelected ? '#fff3e0' : '#fff',
                                    border: isSelected ? '2px solid #ffb74d' : '1px solid #f0f0f0',
                                    color: isSelected ? '#e65100' : '#757575',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: isSelected ? '#ffe0b2' : '#f5f5f5', transform: 'translateY(-1px)' }
                                }}
                                onClick={() => setMealName(meal.id)}
                            >
                                <i className={meal.icon} style={{ fontSize: '20px' }} />
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: isSelected ? '700' : '500' }}>{meal.title}</Typography>
                            </IconButton>
                        );
                    })}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, backgroundColor: '#fcfcfc', borderTop: '1px solid #f0f0f0' }}>
                <Button onClick={onClose} variant="text" sx={{ color: 'text.secondary', fontWeight: '600' }}>Cancel</Button>
                <Button onClick={saveEventMealSettings} variant="contained" disableElevation sx={{ borderRadius: '8px', px: 3, fontWeight: '600', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
                    Save Schedule
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MealDialogEditDates;